"use client";

import React from "react";
import Image from "next/image";
import styles from "./VideoCard.module.scss";
import Text from "@components/atoms/Text";
import { VideoCardProps } from "@interfaces/video";
import { generateThumbnailUrl } from "@utils/videoUtils";
import assetLoader from "@utils/internalImageLoder";
import { getLoginUrl, addCaptureIntentIfNeeded } from "@utils/getLoginUrl";
import { EventType, trackEventWithCommonProps } from "@utils/eventTracker";
import { LoginSdkConfig } from '@enums/LoginSdkConfig';
import { handleLoginFlow } from "@utils/loginFlowHandler";

// Icons

const VideoCard: React.FC<VideoCardProps> = ({
  video,
  metadata,
  onClick,
  className,
  isLoggedIn,
  brandName,
  modelName,
  cityName,
  carAvailability,
  pageType,
}) => {
  const handleClick = async () => {
    trackEventWithCommonProps("action", EventType.TRACK_EVENT, {
      pageType: pageType,
      additionalProps: {
        action_type: "tap_btn",
        action_name: "tap_btn_expert_video",
        video_locked: video?.locked,
        video_tag: metadata?.title || "",
        car_availability: carAvailability,
        city_name: cityName,
        vehicle_make: brandName,
        vehicle_model: modelName,
      },
    });

    if (video?.locked && !isLoggedIn) {
      if (typeof window !== "undefined") {
        sessionStorage.setItem("loginType", "Expert video");
        localStorage.setItem(
          "MMV",
          JSON.stringify({
            make: brandName,
            model: modelName,
          })
        );
      }
      const currentUrl = addCaptureIntentIfNeeded(
        window.location.href,
        { make: brandName, model: modelName },
        "Expert video",
        pageType as any
      );
      await handleLoginFlow(getLoginUrl(currentUrl), LoginSdkConfig.EXCLUSIVE_VIDEO);
    } else {
      onClick?.(video, metadata);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      handleClick();
    }
  };

  const thumbnailUrl = generateThumbnailUrl(video, metadata.thumbnail);

  return (
    <div
      className={`${styles.container} ${className || ""}`}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      role="button"
      tabIndex={0}
    >
      <div className={styles.videoThumbnail}>
        <Image
          src={thumbnailUrl}
          alt={`Thumbnail for ${metadata.title}`}
          className={styles.thumbnailImage}
          width={50}
          height={50}
          loading="lazy"
        />

        <div className={styles.playOverlay}>
          <Image
            src={`${assetLoader()}/icons/thumbnail-play.svg`}
            alt="Play"
            loading="lazy"
            width={32}
            height={32}
          />
        </div>

        <div className={styles.bottomOverlay} />

        {video?.locked && (
          <div className={styles.exclusiveBadge}>
            <Image
              src={`${assetLoader()}/icons/icon-exclusive.svg`}
              alt="Exclusive"
              width={12}
              height={12}
            />
            Exclusive
          </div>
        )}
        <div className={styles.videoContent}>
          <Text level="p" className={styles.videoTitle}>{metadata?.title}</Text>
        </div>
      </div>
    </div>
  );
};

export default VideoCard;
